import { useState, useEffect, useCallback } from "react";
import parentI18n, { setParentLocale } from "features/parent/i18n";

// ローカルストレージキー
const AUTH_LOCALE_KEY = "auth_parent_locale";

// 保護者用言語オプション（保護者画面はひらがななし）
export const PARENT_LOCALE_OPTIONS = [
  { value: "ja_kanji", label: "漢字" },
  { value: "en", label: "English" },
  { value: "zh_tw", label: "繁體中文" },
] as const;

export const PARENT_LOCALE_LABELS: Record<string, string> = {
  ja_kanji: "漢字",
  en: "English",
  zh_tw: "繁體中文",
};

// 有効な保護者用ロケールかチェック
const isValidParentLocale = (locale: string): boolean => {
  return PARENT_LOCALE_OPTIONS.some((option) => option.value === locale);
};

/**
 * デバイス/ブラウザの言語設定に基づいてデフォルトロケールを取得する
 * - ネイティブアプリ: window.kidsAppHostLanguageCode を使用
 * - ウェブ環境: navigator.language を使用
 * - 保護者用画面では ja は ja_kanji に変換
 * - デフォルトは ja_kanji
 */
export const getDefaultParentLocale = (): string => {
  const getLocaleFromLangCode = (lang: string): string => {
    const langCode = lang.split("-")[0].toLowerCase();
    if (langCode === "en") {
      return "en";
    }
    if (langCode === "zh") {
      return "zh_tw";
    }
    // 日本語は漢字表記（保護者用はひらがななし）
    return "ja_kanji";
  };

  // ネイティブアプリの場合は、デバイス言語設定を使用
  if (window.kidsAppHostLanguageCode) {
    return getLocaleFromLangCode(window.kidsAppHostLanguageCode);
  }

  // ウェブ環境の場合はブラウザの言語設定を使用
  return getLocaleFromLangCode(navigator.language || "");
};

/**
 * parentI18n.locale を API language フィールドに変換
 * API expects: { locale: "ja" | "en" | "zh", format: "kanji" | "hiragana" | ... }
 */
export const mapLocaleToLanguage = (parentLocale: string): { locale: string; format: string } => {
  switch (parentLocale) {
    case "ja_kanji":
      return { locale: "ja", format: "kanji" };
    case "ja_hiragana":
      return { locale: "ja", format: "hiragana" };
    case "en":
      return { locale: "en", format: "en" };
    case "zh_tw":
      return { locale: "zh", format: "tw" };
    case "zh_cn":
      return { locale: "zh", format: "cn" };
    default:
      return { locale: "ja", format: "kanji" };
  }
};

/**
 * 認証画面用のlocale管理フック
 * - localStorage でユーザーの言語選択を保存
 * - parentI18n と連携して言語を切り替え
 */
export const useAuthLocale = () => {
  const [currentLocale, setCurrentLocale] = useState<string>(() => {
    // 初期化時のロジック
    const savedLocale = localStorage.getItem(AUTH_LOCALE_KEY);
    if (savedLocale && isValidParentLocale(savedLocale)) {
      // キーがあれば、それを使用
      if (parentI18n.locale !== savedLocale) {
        setParentLocale(savedLocale);
      }
      return savedLocale;
    } else {
      // キーがなければ、デバイス言語で初期化
      const deviceLocale = getDefaultParentLocale();
      localStorage.setItem(AUTH_LOCALE_KEY, deviceLocale);
      setParentLocale(deviceLocale);
      return deviceLocale;
    }
  });

  // 初回マウント時に parentI18n のlocaleを同期
  useEffect(() => {
    if (parentI18n.locale !== currentLocale) {
      setParentLocale(currentLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 言語切り替え関数
  const changeLocale = useCallback((locale: string) => {
    if (!isValidParentLocale(locale)) {
      return;
    }
    localStorage.setItem(AUTH_LOCALE_KEY, locale);
    setParentLocale(locale);
    setCurrentLocale(locale);
  }, []);

  return {
    currentLocale,
    changeLocale,
    localeOptions: PARENT_LOCALE_OPTIONS,
    localeLabels: PARENT_LOCALE_LABELS,
  };
};
